/**
 * VoucherDetail —— 憑單詳情（`ui-spec.md` P-07）
 * 睇：相／分錄（借貸）／OCR 原文／狀態／代墊；⏳ 沖銷（void）留 P1-9。
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import TopBar from '../components/TopBar';
import { api, fmtMoney, type Me, type VoucherDetailData } from '../lib/api';

export default function VoucherDetail({ me }: { me: Me }) {
  const { t } = useTranslation();
  const { id = '' } = useParams();
  const [data, setData] = useState<VoucherDetailData | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [voidErr, setVoidErr] = useState<string | null>(null);
  const [voidBusy, setVoidBusy] = useState(false);
  const [justVoided, setJustVoided] = useState<{ reversalNo: string; reversalId: string } | null>(null);

  const load = () => api.voucher(id).then(setData).catch((e: unknown) => setErr(e instanceof Error ? e.message : String(e)));
  useEffect(() => { void load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [id]);

  const canVoid = (me.role === 'founder' || me.role === 'cofounder')
    && data?.voucher.status !== 'voided' && !data?.voucher.reversal_of;

  const doVoid = async () => {
    setVoidBusy(true); setVoidErr(null);
    try {
      const r = await api.voidVoucher(id, reason);
      setJustVoided({ reversalNo: r.reversalNo, reversalId: r.reversalId });
      setReason('');
      await load();
    } catch (e) {
      setVoidErr(e instanceof Error ? e.message : String(e));
    } finally { setVoidBusy(false); }
  };

  if (err) return <main className="page"><p className="err">{err}</p><Link to="/vouchers">{t('detail.back')}</Link></main>;
  if (!data) return <main className="page"><p className="note">{t('list.loading')}</p></main>;

  const v = data.voucher;
  const ocr = data.ocr[0];
  let parsed: Record<string, unknown> | null = null;
  if (ocr?.parsed_json) { try { parsed = JSON.parse(ocr.parsed_json) as Record<string, unknown>; } catch { parsed = null; } }

  return (
    <main className="page">
      <TopBar title={v.voucher_no} />

      <section className="card">
        <div className="totals">
          <div><small>{t('detail.amount')}</small><b className={v.kind === 'receipt' ? 'pos' : 'neg'}>{fmtMoney(v.amount_cents)}</b></div>
          <div><small>{t('expense.date')}</small><b>{v.txn_date}</b></div>
          <div><small>{t('detail.status')}</small>
            <b className={v.status === 'voided' ? 'neg' : ''}>{t(`detail.status_${v.status}`)}</b>
          </div>
        </div>
        {(v.reversal_of || data.reversal) && (
          <p className="note">
            {data.reversal && (
              <>🔁 {t('detail.voided_by')}：<Link to={`/vouchers/${data.reversal.id}`}>{data.reversal.voucher_no}</Link>　</>
            )}
            {data.original && (
              <>↩️ {t('detail.reversal_of')}：<Link to={`/vouchers/${data.original.id}`}>{data.original.voucher_no}</Link></>
            )}
          </p>
        )}
        {justVoided && (
          <p className="ok">
            ✅ {t('detail.void_done', { no: justVoided.reversalNo })}{' '}
            <Link to={`/vouchers/${justVoided.reversalId}`}>{t('detail.open_reversal')}</Link>
          </p>
        )}
        <ul className="kv">
          <li>{t('expense.purpose')}：{v.purpose}</li>
          <li>{t('expense.paid_by')}：{v.paid_by === 'company' ? t('expense.paid_by_company') : t(`expense.paid_by_${v.paid_by}`)}
            ／{t('expense.payment_method')}：{t(`method.${v.payment_method}`)}</li>
          {v.reimbursement_status === 'pending' && <li className="neg">{t('list.pending_reimb')}</li>}
          {v.doc_no ? <li>{t('income.doc_no')}：{String(v.doc_no)}</li> : null}
        </ul>
      </section>

      <section className="card">
        <h2>{t('detail.lines')}</h2>
        <table className="ledger">
          <thead>
            <tr><th>#</th><th>{t('detail.gl')}</th><th className="num">{t('detail.dr')}</th><th className="num">{t('detail.cr')}</th></tr>
          </thead>
          <tbody>
            {data.lines.map((l) => (
              <tr key={l.line_no}>
                <td>{l.line_no}</td>
                <td>{l.gl_code}{l.memo ? ` · ${l.memo}` : ''}</td>
                <td className="num">{l.dr_cents ? fmtMoney(l.dr_cents) : ''}</td>
                <td className="num">{l.cr_cents ? fmtMoney(l.cr_cents) : ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="note">{t('detail.balanced')}</p>
      </section>

      <section className="card">
        <h2>{t('detail.attachments', { n: data.attachments.length })}</h2>
        {data.attachments.length === 0 && <p className="note">{t('detail.no_attachment')}</p>}
        <div className="row">
          {data.attachments.map((a) => (
            a.mime === 'application/pdf'
              ? <a key={a.id} className="cta secondary" href={`/api/attachments/${a.id}`} target="_blank" rel="noreferrer">📄 PDF</a>
              : <a key={a.id} href={`/api/attachments/${a.id}`} target="_blank" rel="noreferrer">
                  <img className="thumb" src={`/api/attachments/${a.id}`} alt={a.kind} />
                </a>
          ))}
        </div>
      </section>

      {ocr && (
        <section className="card">
          <h2>🤖 {t('ocr.heading')}</h2>
          <p className="note">{t('detail.ocr_meta', { model: ocr.model, confidence: ocr.confidence ?? '—' })}</p>
          {parsed && (
            <ul className="kv">
              {Object.entries(parsed).map(([k, val]) => (
                <li key={k}>{k}：{val === null ? '—' : String(val)}</li>
              ))}
            </ul>
          )}
          <p className="note">{t('detail.raw_note')}</p>
        </section>
      )}

      {/* 紅字沖銷（P1-9）：唔准刪，只准沖銷；一定要填原因 */}
      {canVoid && !justVoided && (
        <section className="card">
          <h2>{t('detail.void_heading')}</h2>
          <label className="field">
            <span>{t('detail.void_reason')} *</span>
            <textarea
              rows={2} value={reason} onChange={(e) => setReason(e.target.value)}
              placeholder={t('detail.void_reason_hint')} disabled={voidBusy}
            />
          </label>
          {voidErr && <p className="err">{voidErr}</p>}
          <button className="cta danger" onClick={() => void doVoid()} disabled={voidBusy || reason.trim().length < 3}>
            🔁 {t('detail.void_cta')}
          </button>
          <p className="note">{t('detail.void_note')}</p>
        </section>
      )}
      {v.status === 'voided' && (
        <section className="card">
          <h2>{t('detail.void_heading')}</h2>
          <p className="err">{t('detail.void_already')}</p>
        </section>
      )}
    </main>
  );
}
